// ProfilePosts.jsx
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
// Supabase
import { supabase } from "../../supabase/supabase";
import {
  FaHeart,
  FaRegHeart,
  FaShareAlt,
  FaGift,
  FaBookmark,
  FaRetweet,
  FaEllipsisV,
  FaEyeSlash,
  FaBan,
  FaFlag,
  FaEdit,
  FaTrash,
  FaChartLine,
  FaCommentSlash,
  FaSlash,
  FaExpand,
  FaArrowLeft,
  FaComment,
  FaRegComment,
  FaPaperPlane,
  FaTimes,
  FaRegImages,
  FaCamera,
  FaMagic,
  FaMicrophone,
  FaRegFileAudio
} from "react-icons/fa";
import "./ProfilePosts.css";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import useAuthListener from "../../hooks/useAuthListener";
import { useLanguage } from "../../contexts/LanguageContext";
import { trackPostView, trackPostViewRPC } from "../../utils/trackPostView";

const DEFAULT_USER_ICON =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNmM2Y0ZjYiLz4KPHN2ZyB4PSIxMCIgeT0iMTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM5Y2EzYWYiIHN0cm9rZS13aWR0aD0iMiIgc3Rya2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPgo8cGF0aCBkPSJNMjAgMjF2LTJhNCA0IDAgMCAwLTQtNEg4YTQgNCAwIDAgMC00IDR2MiIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz4KPC9zdmc+Cjwvc3ZnPg==";

const getDefaultUserIcon = () => DEFAULT_USER_ICON;

// Helper function to resolve avatar URL
const resolveAvatarUrl = (filename, userFolderId) => {
  if (!filename) return getDefaultUserIcon();
  
  // if already a full url, return it
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
    return filename;
  }
  // if data url, return it
  if (typeof filename === 'string' && filename.startsWith('data:')) return filename;

  // Construct the proper path: avatars/<userId>/<filename>
  const path = `${userFolderId}/${filename}`;
  
  try {
    const { data } = supabase.storage.from('avatars').getPublicUrl(path);
    if (data?.publicUrl) {
      return data.publicUrl;
    }
  } catch (e) {
    console.log('[ProfilePosts] Error resolving avatar URL:', e);
  }

  return getDefaultUserIcon();
};

const HIDDEN_POSTS_KEY = 'feed_hidden_posts';
const BLOCKED_USERS_KEY = 'blocked_user_ids';
const REPORT_REASONS = [
  { value: 'spam', label: 'Spam', description: 'Misleading or repetitive content' },
  { value: 'false_information', label: 'False information', description: 'Factually incorrect content' },
  { value: 'hate_speech', label: 'Hate speech', description: 'Content that promotes violence or discrimination' },
  { value: 'harassment', label: 'Harassment', description: 'Bullying or abusive behavior' },
  { value: 'violence', label: 'Violence', description: 'Content that promotes or glorifies violence' },
  { value: 'copyright violation', label: 'Copyright violation', description: 'Unauthorized use of copyrighted material' },
  { value: 'other', label: 'Other', description: 'Another reason not listed above' }
];

const POST_MEDIA_BASE_URL = 'https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media';

export default function ProfilePosts() {
  const { id } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthListener();
  const qs = new URLSearchParams(search);
  const tab = qs.get("tab") || "posts";
  const scrollTo = qs.get("scrollTo");

  const [pageUser, setPageUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [savedPosts, setSavedPosts] = useState(new Set());
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [commentsOverlayPostId, setCommentsOverlayPostId] = useState(null);
  const [commentsData, setCommentsData] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState(null);
  const { translations: tRaw } = useLanguage();
  const t = useMemo(() => tRaw?.profilePosts || {}, [tRaw]);

  const authUserId = useMemo(() => {
    return user?.uid || user?.id || user?.user_id || null;
  }, [user]);

  const [likeDetailsPost, setLikeDetailsPost] = useState(null);
  const [likeDetailsUsers, setLikeDetailsUsers] = useState([]);
  const [likeDetailsLoading, setLikeDetailsLoading] = useState(false);
  const [likeDetailsError, setLikeDetailsError] = useState(null);
  const [isLikeDetailsOpen, setIsLikeDetailsOpen] = useState(false);
  const [followingSet, setFollowingSet] = useState(new Set());
  const [followLoadingMap, setFollowLoadingMap] = useState({});

  const [hiddenPostIds, setHiddenPostIds] = useState(() => {
    try {
      if (typeof window === 'undefined') return new Set();
      const stored = window.localStorage.getItem(HIDDEN_POSTS_KEY);
      if (!stored) return new Set();
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(parsed);
      }
    } catch (e) {
      console.warn('[ProfilePosts] Failed to restore hidden posts:', e);
    }
    return new Set();
  });
  const hiddenPostIdsRef = useRef(hiddenPostIds);

  const [hideConfirmPost, setHideConfirmPost] = useState(null);
  const [reportPost, setReportPost] = useState(null);
  const [selectedReportReason, setSelectedReportReason] = useState(null);
  const [reportDescription, setReportDescription] = useState("");
  const [reportError, setReportError] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [blockConfirmUser, setBlockConfirmUser] = useState(null);
  const [blockError, setBlockError] = useState("");
  const [blockSubmitting, setBlockSubmitting] = useState(false);
  const [deleteConfirmPost, setDeleteConfirmPost] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [editPost, setEditPost] = useState(null);

  const isViewingOwnProfile = useMemo(() => {
    if (!authUserId || !pageUser?.id) return false;
    return authUserId === pageUser.id;
  }, [authUserId, pageUser?.id]);

  const ownsPost = useCallback((post) => {
    if (!post) return false;
    const ownerId = post.user_id || post.postedById || post.postedByID || post.userId;
    return ownerId ? ownerId === authUserId : false;
  }, [authUserId]);

  useEffect(() => {
    if (!authUserId) {
      setFollowingSet(new Set());
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', authUserId);

        if (error) {
          console.warn('[ProfilePosts] Failed to fetch following list:', error);
          if (isMounted) setFollowingSet(new Set());
          return;
        }

        const nextSet = new Set((data || []).map(row => row.following_id).filter(Boolean));
        if (isMounted) setFollowingSet(nextSet);
      } catch (err) {
        console.warn('[ProfilePosts] Error loading following list:', err);
        if (isMounted) setFollowingSet(new Set());
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [authUserId]);

  const handleEditPostRequest = useCallback((post) => {
    if (!post) return;
    if (!isViewingOwnProfile || !ownsPost(post)) return;
    setEditPost(post);
    setOpenMenu(null);
  }, [isViewingOwnProfile, ownsPost]);

  const handleDeletePostRequest = useCallback((post) => {
    if (!post) return;
    if (!isViewingOwnProfile || !ownsPost(post)) return;
    setDeleteConfirmPost(post);
    setDeleteError("");
    setDeleteSubmitting(false);
    setOpenMenu(null);
  }, [isViewingOwnProfile, ownsPost]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmPost(null);
    setDeleteError("");
    setDeleteSubmitting(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirmPost || deleteSubmitting) return;

    const postId = deleteConfirmPost.id;
    setDeleteSubmitting(true);
    setDeleteError("");

    try {
      // Ensure user is authenticated
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData?.session) {
        throw new Error('User must be authenticated to delete posts');
      }

      // Call the edge function to delete the post (handles cascading deletes)
      // According to API docs, body should use 'postId' (camelCase)
      const { data, error } = await supabase.functions.invoke('delete-post', {
        body: { postId: postId }
      });

      if (error) {
        throw error;
      }
      
      // Check if the response indicates an error (from API docs: error responses have 'error' field)
      if (data?.error) {
        throw new Error(data.error || 'Failed to delete post');
      }

      // Check for success response (from API docs: success responses have 'success: true')
      if (!data?.success) {
        throw new Error('Delete operation did not complete successfully');
      }

      console.log('[ProfilePosts] Post deleted successfully:', data);

      // Update UI state after successful deletion
      setPosts((prev) => {
        const next = prev.filter((p) => p.id !== postId);
        if (selectedPostId === postId) {
          const fallback = next[0]?.id || null;
          setSelectedPostId(fallback);
        }
        return next;
      });

      setLikedPosts((prev) => {
        if (!prev.has(postId)) return prev;
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });

      setSavedPosts((prev) => {
        if (!prev.has(postId)) return prev;
        const next = new Set(prev);
        next.delete(postId);
        return next;
      });

      if (commentsOverlayPostId === postId) {
        setCommentsOverlayPostId(null);
        setCommentsData([]);
        setCommentsError(null);
      }

      setDeleteConfirmPost(null);
    } catch (err) {
      console.error('[ProfilePosts] Failed to delete post:', err);
      
      // Extract error message following API docs error response format
      let errorMessage = 'Unable to delete this post right now.';
      
      if (err?.message) {
        errorMessage = err.message;
      } else if (err?.error) {
        errorMessage = typeof err.error === 'string' ? err.error : err.error.message || errorMessage;
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      
      setDeleteError(errorMessage);
    } finally {
      setDeleteSubmitting(false);
    }
  }, [commentsOverlayPostId, deleteConfirmPost, deleteSubmitting, selectedPostId]);

  const handleCloseEdit = useCallback(() => {
    setEditPost(null);
  }, []);

  const handlePostUpdated = useCallback((patch) => {
    if (!patch?.id) return;
    setPosts((prev) =>
      prev.map((p) =>
        p.id === patch.id
          ? {
              ...p,
              caption: patch.caption,
              media_names: patch.media_names,
              audio_filename: patch.audio_filename ?? null,
              media: Array.isArray(patch.media) ? patch.media : p.media
            }
          : p
      )
    );
    setEditPost(null);
  }, []);

  const handleInsightsRequest = useCallback((post) => {
    if (!post) return;
    if (!isViewingOwnProfile || !ownsPost(post)) return;
    navigate(`/insights/${post.id}`);
  }, [isViewingOwnProfile, ownsPost, navigate]);

  const handleToggleCommentsRequest = useCallback(async (post) => {
    if (!post?.id) return;
    if (!isViewingOwnProfile || !ownsPost(post)) return;

    const postId = post.id;
    const currentPost = posts.find((p) => p.id === postId);
    const previousValue = currentPost ? currentPost.is_comments_enabled !== false : true;
    const nextValue = !previousValue;

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, is_comments_enabled: nextValue } : p
      )
    );

    try {
      const { error } = await supabase
        .from('posts')
        .update({ is_comments_enabled: nextValue })
        .eq('id', postId);

      if (error) {
        throw error;
      }
    } catch (err) {
      console.error('[ProfilePosts] Failed to toggle comments:', err);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, is_comments_enabled: previousValue } : p
        )
      );
    }
  }, [isViewingOwnProfile, ownsPost, posts]);

  const handleToggleRepostsRequest = useCallback(async (post) => {
    if (!post?.id) return;
    if (!isViewingOwnProfile || !ownsPost(post)) return;

    const postId = post.id;
    const currentPost = posts.find((p) => p.id === postId);
    const previousValue = currentPost ? currentPost.is_reposts_enabled !== false : true;
    const nextValue = !previousValue;

    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, is_reposts_enabled: nextValue } : p
      )
    );

    try {
      const { error } = await supabase
        .from('posts')
        .update({ is_reposts_enabled: nextValue })
        .eq('id', postId);

      if (error) {
        throw error;
      }
    } catch (err) {
      console.error('[ProfilePosts] Failed to toggle reposts:', err);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, is_reposts_enabled: previousValue } : p
        )
      );
    }
  }, [isViewingOwnProfile, ownsPost, posts]);

  const generateShareId = useCallback(() => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let id = '';
    for (let i = 0; i < 8; i += 1) {
      const idx = Math.floor(Math.random() * chars.length);
      id += chars[idx];
    }
    return id;
  }, []);

  const createShareLink = useCallback(async (post) => {
    if (!post?.id) throw new Error('Missing post data');
    if (!authUserId) {
      alert(t?.shareRequiresLogin || 'Please sign in to share this post.');
      return null;
    }

    const ownerId = post.postedById || post.postedByID || post.user_id || post.userId;
    const ownerUsername = post.postedByUsername || pageUser?.username;
    if (!ownerId || !ownerUsername) throw new Error('Missing post owner information');

    let lastError = null;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const shareId = generateShareId();
      const shareBase = process.env.REACT_APP_SHARE_BASE_URL;
      // SPA view URL (used within site and stored to DB)
      const viewUrl = (typeof window !== 'undefined')
        ? `${window.location.origin.replace(/\/$/, '')}/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}/p`
        : `/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}/p`;
      // External share URL (for social crawlers); fall back to view URL if base missing
      const shareUrl = shareBase
        ? `${shareBase.replace(/\/$/, '')}/@${encodeURIComponent(ownerUsername)}/${post.id}/${shareId}/p`
        : viewUrl;
      const payload = {
        post_id: post.id,
        share_id: shareId,
        share_url: viewUrl,
        sharer_id: authUserId
      };

      const { error } = await supabase.from('shares').insert(payload);
      if (!error) {
        return {
          shareUrl,
          viewUrl,
          shareId
        };
      }

      lastError = error;
      if (!error?.code || error.code !== '23505') {
        break;
      }
    }

    console.error('[ProfilePosts] Failed to create share record:', lastError);
    throw lastError || new Error('Unable to create share link');
  }, [authUserId, generateShareId, pageUser?.username, t]);

  const sharePost = useCallback(async (post) => {
    try {
      const result = await createShareLink(post);
      if (!result) return;
      const shareUrl = typeof result === 'string' ? result : result.shareUrl || result.viewUrl;
      if (!shareUrl) return;
      const title = t?.shareTitle || `Check out ${post?.postedByUsername || 'this'} post!`;

      if (navigator.share) {
        try {
          await navigator.share({ title, url: shareUrl });
        } catch (shareError) {
          console.warn('[ProfilePosts] navigator.share failed, falling back to clipboard', shareError);
          await navigator.clipboard.writeText(shareUrl);
          alert(t?.shareCopied || 'Link copied to clipboard!');
        }
      } else {
        await navigator.clipboard.writeText(shareUrl);
        alert(t?.shareCopied || 'Link copied to clipboard!');
      }
    } catch (error) {
      console.error('[ProfilePosts] Error sharing post:', error);
      alert(t?.shareError || 'Unable to share this post right now.');
    }
  }, [createShareLink, t]);

  const openLikeDetails = useCallback(async (post) => {
    if (!post?.id) return;
    setLikeDetailsPost(post);
    setIsLikeDetailsOpen(true);
    setLikeDetailsLoading(true);
    setLikeDetailsError(null);

    try {
      const { data: likesData, error: likesError } = await supabase
        .from('likes')
        .select('user_id')
        .eq('post_id', post.id);

      if (likesError) {
        throw likesError;
      }

      const userIds = [...new Set((likesData || []).map(row => row.user_id).filter(Boolean))];

      if (!userIds.length) {
        setLikeDetailsUsers([]);
        return;
      }

      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, display_name, username, avatar_url')
        .in('user_id', userIds);

      if (profilesError) {
        throw profilesError;
      }

      const mapped = (profilesData || []).map(profile => ({
        id: profile.user_id,
        username: profile.display_name || profile.username || `user_${profile.user_id?.slice(0, 8)}`,
        avatar: resolveAvatarUrl(profile.avatar_url, profile.user_id),
        isFollowed: followingSet.has(profile.user_id)
      }));

      setLikeDetailsUsers(mapped);
    } catch (error) {
      console.error('[ProfilePosts] Failed to load like details:', error);
      setLikeDetailsUsers([]);
      setLikeDetailsError(error?.message || 'Unable to load likes right now.');
    } finally {
      setLikeDetailsLoading(false);
    }
  }, [followingSet]);

  const closeLikeDetails = useCallback(() => {
    setIsLikeDetailsOpen(false);
    setLikeDetailsPost(null);
    setLikeDetailsUsers([]);
    setLikeDetailsError(null);
  }, []);

  useEffect(() => {
    if (!isLikeDetailsOpen) return;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeLikeDetails();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isLikeDetailsOpen, closeLikeDetails]);

  useEffect(() => {
    if (isLikeDetailsOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isLikeDetailsOpen]);

  const toggleFollowUser = useCallback(async (targetId, isCurrentlyFollowed) => {
    if (!authUserId || !targetId || targetId === authUserId) return;

    setFollowLoadingMap(prev => ({ ...prev, [targetId]: true }));

    try {
      if (isCurrentlyFollowed) {
        const { error } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', authUserId)
          .eq('following_id', targetId);

        if (error) {
          throw error;
        }

        setFollowingSet(prev => {
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
      } else {
        const { error } = await supabase
          .from('follows')
          .insert({
            follower_id: authUserId,
            following_id: targetId
          });

        if (error) {
          throw error;
        }

        setFollowingSet(prev => {
          const next = new Set(prev);
          next.add(targetId);
          return next;
        });
      }

      setLikeDetailsUsers(prev =>
        prev.map(user =>
          user.id === targetId ? { ...user, isFollowed: !isCurrentlyFollowed } : user
        )
      );
    } catch (error) {
      console.error('[ProfilePosts] Failed to toggle follow:', error);
    } finally {
      setFollowLoadingMap(prev => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
    }
  }, [authUserId]);


  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        console.log('[ProfilePosts] Loading user profile and posts from Supabase...');
        
        // Fetch user profile from Supabase
        const { data: userProfile, error: userError } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', id)
          .single();

        if (userError || !userProfile) {
          console.error('[ProfilePosts] User not found:', userError);
          return;
        }

        // Transform user profile to match expected format
        const transformedUser = {
          id: userProfile.user_id,
          username: userProfile.display_name || userProfile.username || `user_${id.slice(0, 8)}`,
          profileImage: resolveAvatarUrl(userProfile.avatar_url, userProfile.user_id)
        };
        setPageUser(transformedUser);

        if (tab === "posts") {
          // Fetch user's posts from Supabase
          // Filter out locked and private posts
          const { data: postsData, error: postsError } = await supabase
            .from('posts')
            .select('*')
            .eq('user_id', id)
            .not('is_locked', 'eq', true)
            .not('is_private', 'eq', true)
            .order('created_at', { ascending: false });

          if (postsError) {
            console.error('[ProfilePosts] Error fetching posts:', postsError);
            return;
          }

          const postIds = postsData.map(post => post.id).filter(Boolean);
          const likeCountMap = new Map();
          const commentCountMap = new Map();

          if (postIds.length) {
            try {
              const [likesResponse, commentsResponse] = await Promise.all([
                supabase
                  .from('likes')
                  .select('post_id')
                  .in('post_id', postIds),
                supabase
                  .from('comments')
                  .select('post_id')
                  .in('post_id', postIds)
              ]);

              const { data: likesRows, error: likesError } = likesResponse || {};
              if (likesError) {
                console.error('[ProfilePosts] Error fetching like counts:', likesError);
              } else if (Array.isArray(likesRows)) {
                likesRows.forEach(row => {
                  if (!row?.post_id) return;
                  likeCountMap.set(row.post_id, (likeCountMap.get(row.post_id) || 0) + 1);
                });
              }

              const { data: commentsRows, error: commentsError } = commentsResponse || {};
              if (commentsError) {
                console.error('[ProfilePosts] Error fetching comment counts:', commentsError);
              } else if (Array.isArray(commentsRows)) {
                commentsRows.forEach(row => {
                  if (!row?.post_id) return;
                  commentCountMap.set(row.post_id, (commentCountMap.get(row.post_id) || 0) + 1);
                });
              }
            } catch (countsError) {
              console.error('[ProfilePosts] Error loading engagement counts:', countsError);
            }
          }

          // Transform posts to match expected format
          const transformedPosts = postsData.map(post => {
            const defaultCommentCount = Number.isFinite(Number(post.comment_count)) ? Number(post.comment_count) : 0;
            const likeCount = likeCountMap.get(post.id);
            const commentCount = commentCountMap.get(post.id);

            return {
              ...post,
              postedById: post.user_id,
              postedByUsername: transformedUser.username,
              postedByProfileImg: transformedUser.profileImage,
              timestamp: post.created_at,
              media: post.media_names ? post.media_names.map((filename, index) => ({
                type: filename.includes('.mp4') || filename.includes('.mov') ? 'video' : 'image',
                url: `https://dtskgpqvvxcbtcsmedrq.supabase.co/storage/v1/object/public/post-media/${post.user_id}/${filename}`,
                index
              })) : [],
              audioUrl: post.audio_filename ? `${POST_MEDIA_BASE_URL}/${post.user_id}/${post.audio_filename}` : null,
              like_count: typeof likeCount === 'number' ? likeCount : 0,
              comment_count: typeof commentCount === 'number' ? commentCount : defaultCommentCount,
              isLocked: post.is_locked === true,
              isDeleted: false
            };
          });

          const filteredPosts = transformedPosts.filter(post => !hiddenPostIdsRef.current.has(post.id));
          setPosts(filteredPosts);
          const initialSelectedId = (scrollTo && filteredPosts.some(post => post.id === scrollTo))
            ? scrollTo
            : filteredPosts[0]?.id || null;
          setSelectedPostId(initialSelectedId);
        } else {
          // For reposts, we'll need to implement this based on your reposts table structure
          // For now, we'll show an empty state
          console.log('[ProfilePosts] Reposts not implemented yet');
          setPosts([]);
          setSelectedPostId(null);
        }
      } catch (error) {
        console.error('[ProfilePosts] Error loading data:', error);
      }
    })();
  }, [id, tab, scrollTo]);


  const selectedPost = useMemo(
    () => posts.find(p => p.id === selectedPostId) || null,
    [posts, selectedPostId]
  );

  // Track view when a post is selected in ProfilePosts
  useEffect(() => {
    if (!selectedPostId || !selectedPost || !user?.id) return;
    
    // Don't track views for the post owner viewing their own post
    const postOwnerId = selectedPost.user_id || selectedPost.postedById;
    const viewerId = user.uid || user.id || user.user_id;
    
    if (postOwnerId === viewerId) return;

    // Track view when post is selected
    (async () => {
      try {
        await trackPostViewRPC(selectedPostId, viewerId);
      } catch {
        await trackPostView(selectedPostId, viewerId);
      }
    })();
  }, [selectedPostId, selectedPost, user]);

  const sidePosts = useMemo(() => {
    const hasValidMedia = (p) => {
      const raw = Array.isArray(p.media) && p.media.length
        ? p.media
        : Array.isArray(p.compressedMedia)
        ? p.compressedMedia
        : [];
      const entries = raw
        .map(r => (typeof r === "string" ? JSON.parse(r) : r))
        .filter(Boolean);
      return entries.some(e => (e?.type === "image" || e?.type === "video") && e?.url);
    };
    return posts.filter(p => p.id !== selectedPostId && hasValidMedia(p));
  }, [posts, selectedPostId]);

  // Load user's liked and saved posts from Supabase
  useEffect(() => {
    if (!authUserId) return;

    const loadUserInteractions = async () => {
      try {
        console.log('[ProfilePosts] Loading user interactions from Supabase...');
        
        // Fetch liked posts
        const { data: likedData, error: likedError } = await supabase
          .from('likes')
          .select('post_id')
          .eq('user_id', authUserId);

        // Fetch saved posts
        const { data: savedData, error: savedError } = await supabase
          .from('saves')
          .select('post_id')
          .eq('user_id', authUserId);

        if (likedError) {
          console.error('[ProfilePosts] Error fetching liked posts:', likedError);
        } else {
          const likedSet = new Set(likedData?.map(item => item.post_id).filter(Boolean) || []);
          setLikedPosts(likedSet);
        }

        if (savedError) {
          console.error('[ProfilePosts] Error fetching saved posts:', savedError);
        } else {
          const savedSet = new Set(savedData?.map(item => item.post_id).filter(Boolean) || []);
          setSavedPosts(savedSet);
        }
      } catch (error) {
        console.error("[ProfilePosts] Error loading user interactions:", error);
      }
    };

    loadUserInteractions();
  }, [authUserId]);

  // Handle fullscreen image functions
  const openFullscreen = (imageSrc) => {
    if (imageSrc) {
      setFullscreenImage(imageSrc);
    }
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
  };

  useEffect(() => {
    hiddenPostIdsRef.current = hiddenPostIds;
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(HIDDEN_POSTS_KEY, JSON.stringify(Array.from(hiddenPostIds)));
      }
    } catch (e) {
      console.warn('[ProfilePosts] Failed to persist hidden posts:', e);
    }
  }, [hiddenPostIds]);

  const hidePostById = useCallback((postId) => {
    if (!postId) return;

    setHiddenPostIds(prev => {
      const next = new Set(prev);
      next.add(postId);
      hiddenPostIdsRef.current = next;
      return next;
    });

    setPosts(prev => {
      const nextPosts = prev.filter(post => post.id !== postId);
      if (selectedPostId === postId || !nextPosts.some(post => post.id === selectedPostId)) {
        const fallback = nextPosts[0]?.id || null;
        setSelectedPostId(fallback);
      }
      return nextPosts;
    });

    setOpenMenu(null);
    setHideConfirmPost(null);
  }, [selectedPostId]);

  const handleShowHideModal = useCallback((post) => {
    if (!post) return;
    setHideConfirmPost(post);
    setReportPost(null);
    setSelectedReportReason(null);
    setReportDescription("");
    setReportError("");
    setReportSubmitting(false);
    setOpenMenu(null);
  }, []);

  const handleShowReportModal = useCallback((post) => {
    if (!post) return;
    setReportPost(post);
    setHideConfirmPost(null);
    setSelectedReportReason(null);
    setReportDescription("");
    setReportError("");
    setReportSubmitting(false);
    setOpenMenu(null);
  }, []);

  const handleShowBlockModal = useCallback((post) => {
    const targetUserId = post?.postedById || post?.user_id || pageUser?.id;
    if (!targetUserId) return;
    const targetUsername = post?.postedByUsername || pageUser?.username || '';
    setBlockConfirmUser({ id: targetUserId, username: targetUsername });
    setBlockError("");
    setBlockSubmitting(false);
    setHideConfirmPost(null);
    setReportPost(null);
    setSelectedReportReason(null);
    setReportDescription("");
    setOpenMenu(null);
  }, [pageUser?.id, pageUser?.username]);

  const handleCancelBlock = useCallback(() => {
    setBlockConfirmUser(null);
    setBlockError("");
    setBlockSubmitting(false);
  }, []);

  const handleConfirmBlock = useCallback(async () => {
    if (!blockConfirmUser || blockSubmitting) return;

    if (!authUserId) {
      setBlockError(t?.blockRequiresLogin || 'Please sign in to block users.');
      return;
    }

    const blockedId = blockConfirmUser?.id;
    if (!blockedId) return;

    setBlockError("");
    setBlockSubmitting(true);

    try {
      const payload = {
        blocker_id: authUserId,
        blocked_id: blockedId,
        created_at: new Date().toISOString()
      };

      const { error } = await supabase.from('blocks').insert(payload);
      if (error) {
        const message = (error.message || '').toLowerCase();
        const isDuplicate = error.code === '23505' || message.includes('duplicate');
        if (!isDuplicate) {
          throw error;
        }
      }

      try {
        const stored = window.localStorage.getItem(BLOCKED_USERS_KEY);
        let list = [];
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            list = parsed;
          }
        }
        if (!list.includes(blockedId)) {
          list.push(blockedId);
          window.localStorage.setItem(BLOCKED_USERS_KEY, JSON.stringify(list));
        }
        window.dispatchEvent(new CustomEvent('blocked-users-updated', { detail: { blockedId } }));
      } catch (storageError) {
        console.warn('[ProfilePosts] Failed to persist blocked user ids:', storageError);
      }

      setHiddenPostIds(prev => {
        const next = new Set(prev);
        posts.forEach(post => {
          if (post.user_id === blockedId || post.postedById === blockedId) {
            next.add(post.id);
          }
        });
        hiddenPostIdsRef.current = next;
        return next;
      });

      setPosts(prev => prev.filter(post => post.user_id !== blockedId && post.postedById !== blockedId));
      handleCancelBlock();
      navigate('/feed', { replace: true });
    } catch (error) {
      console.error('[ProfilePosts] Failed to block user:', error);
      setBlockError(t?.blockError || 'Unable to block this user right now.');
      setBlockSubmitting(false);
    }
  }, [authUserId, blockConfirmUser, blockSubmitting, handleCancelBlock, navigate, posts, setHiddenPostIds, t]);

  const handleCancelReport = useCallback(() => {
    setReportPost(null);
    setSelectedReportReason(null);
    setReportDescription("");
    setReportError("");
    setReportSubmitting(false);
  }, []);

  const handleSubmitReport = useCallback(async () => {
    if (!reportPost || !selectedReportReason || reportSubmitting) return;

    if (!authUserId) {
      setReportError(t?.reportRequiresLogin || 'Please sign in to report posts.');
      return;
    }

    setReportError("");
    setReportSubmitting(true);

    try {
      const payload = {
        reporter_id: authUserId,
        post_id: reportPost.id,
        reason: selectedReportReason,
        description: reportDescription.trim() ? reportDescription.trim() : null
      };

      const { error } = await supabase.from('reports').insert(payload);
      if (error) throw error;

      hidePostById(reportPost.id);
      handleCancelReport();
    } catch (error) {
      console.error('[ProfilePosts] Failed to submit report:', error);
      setReportError(t?.reportError || 'Unable to submit report right now.');
    } finally {
      setReportSubmitting(false);
    }
  }, [authUserId, handleCancelReport, hidePostById, reportDescription, reportPost, reportSubmitting, selectedReportReason, t]);

  const closeCommentsOverlay = useCallback(() => {
    setCommentsOverlayPostId(null);
    setCommentsData([]);
    setCommentsError(null);
  }, []);

  const fetchComments = useCallback(async (postId) => {
    if (!postId) return;
    setCommentsLoading(true);
    setCommentsError(null);
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('id, content, created_at, user_id, parent_comment_id, profiles:profiles!comments_user_id_fkey(user_id, username, display_name, avatar_url)')
        .eq('post_id', postId)
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      const commentIds = (data || []).map(comment => comment.id).filter(Boolean);
      const likeCountMap = new Map();
      const likedByCurrentUser = new Set();

      if (commentIds.length) {
        try {
          const { data: likeRows, error: likeError } = await supabase
            .from('comment_likes')
            .select('comment_id, user_id')
            .in('comment_id', commentIds);

          if (likeError) {
            console.error('[ProfilePosts] Error fetching comment likes:', likeError);
          } else if (Array.isArray(likeRows)) {
            likeRows.forEach(row => {
              if (!row?.comment_id) return;
              likeCountMap.set(row.comment_id, (likeCountMap.get(row.comment_id) || 0) + 1);
              if (authUserId && row.user_id === authUserId) {
                likedByCurrentUser.add(row.comment_id);
              }
            });
          }
        } catch (likeCountsError) {
          console.error('[ProfilePosts] Error loading comment like counts:', likeCountsError);
        }
      }

      const replyCountMap = new Map();
      (data || []).forEach(comment => {
        if (comment?.parent_comment_id) {
          replyCountMap.set(comment.parent_comment_id, (replyCountMap.get(comment.parent_comment_id) || 0) + 1);
        }
      });

      const parsed = (data || []).map(comment => {
        const profile = comment.profiles || {};
        return {
          id: comment.id,
          content: comment.content,
          created_at: comment.created_at,
          user_id: comment.user_id,
          parent_comment_id: comment.parent_comment_id,
          author: {
            username: profile.username || profile.display_name || 'Unknown',
            displayName: profile.display_name || profile.username || 'Unknown',
            avatar: resolveAvatarUrl(profile.avatar_url, profile.user_id || comment.user_id)
          },
          likeCount: likeCountMap.get(comment.id) || 0,
          isLikedByCurrentUser: likedByCurrentUser.has(comment.id),
          replyCount: replyCountMap.get(comment.id) || 0
        };
      });

      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comment_count: parsed.length } : p));
      setCommentsData(parsed);
    } catch (error) {
      setCommentsError(error?.message || 'Failed to load comments');
      setCommentsData([]);
    } finally {
      setCommentsLoading(false);
    }
  }, [authUserId, setPosts]);

  const openCommentsOverlay = (postId) => {
    if (!postId) return;
    setCommentsOverlayPostId(postId);
  };

  // Handle escape key and click outside for fullscreen
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && fullscreenImage) {
        closeFullscreen();
      }
    };

    if (fullscreenImage) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [fullscreenImage]);

  useEffect(() => {
    if (!commentsOverlayPostId) return;
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeCommentsOverlay();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [commentsOverlayPostId, closeCommentsOverlay]);

  useEffect(() => {
    if (commentsOverlayPostId) {
      fetchComments(commentsOverlayPostId);
    }
  }, [commentsOverlayPostId, fetchComments]);

  useEffect(() => {
    if (fullscreenImage || commentsOverlayPostId) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [fullscreenImage, commentsOverlayPostId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleUserClick = (userId) => {
    if (userId) {
      navigate(`/profile/${userId}`);
    }
  };

  const adjustPostLikeCount = useCallback((postId, delta) => {
    if (!postId || !delta) return;
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p;
      const current = Number(p.like_count) || 0;
      const next = Math.max(0, current + delta);
      return { ...p, like_count: next };
    }));
  }, []);

  const adjustPostCommentCount = useCallback((postId, delta) => {
    if (!postId || !delta) return;
    setPosts(prev => prev.map(p => {
      if (p.id !== postId) return p;
      const current = Number(p.comment_count) || 0;
      const next = Math.max(0, current + delta);
      return { ...p, comment_count: next };
    }));
  }, []);

  // Optimistic like toggle
  const handleLike = async (postId) => {
    if (!authUserId) return;

    const isCurrentlyLiked = likedPosts.has(postId);
    const delta = isCurrentlyLiked ? -1 : 1;
    // optimistic update first
    setLikedPosts(prev => {
      const next = new Set(prev);
      if (isCurrentlyLiked) next.delete(postId); else next.add(postId);
      return next;
    });
    adjustPostLikeCount(postId, delta);

    try {
      if (isCurrentlyLiked) {
        // Remove like from Supabase
        const { error } = await supabase
          .from('likes')
          .delete()
          .eq('user_id', authUserId)
          .eq('post_id', postId);
        
        if (error) throw error;
      } else {
        // Add like to Supabase
        const { error } = await supabase
          .from('likes')
          .insert({
            user_id: authUserId,
            post_id: postId,
            created_at: new Date().toISOString()
          });
        
        if (error) throw error;
      }
    } catch (error) {
      // rollback on error
      setLikedPosts(prev => {
        const next = new Set(prev);
        if (isCurrentlyLiked) next.add(postId); else next.delete(postId);
        return next;
      });
      adjustPostLikeCount(postId, -delta);
      console.error("[ProfilePosts] Error toggling like:", error);
    }
  };

  // Optimistic save toggle
  const handleSave = async (postId) => {
    if (!authUserId) return;

    const isCurrentlySaved = savedPosts.has(postId);
    // optimistic update first
    setSavedPosts(prev => {
      const next = new Set(prev);
      if (isCurrentlySaved) next.delete(postId); else next.add(postId);
      return next;
    });

    try {
      if (isCurrentlySaved) {
        // Remove save from Supabase
        const { error } = await supabase
          .from('saves')
          .delete()
          .eq('user_id', authUserId)
          .eq('post_id', postId);
        
        if (error) throw error;
      } else {
        // Add save to Supabase
        const { error } = await supabase
          .from('saves')
          .insert({
            user_id: authUserId,
            post_id: postId,
            created_at: new Date().toISOString()
          });
        
        if (error) throw error;
      }
    } catch (error) {
      // rollback on error
      setSavedPosts(prev => {
        const next = new Set(prev);
        if (isCurrentlySaved) next.add(postId); else next.delete(postId);
        return next;
      });
      console.error("[ProfilePosts] Error toggling save:", error);
    }
  };

  if (!pageUser) return <div className="loading">{t.loading || "Loading…"}</div>;

  return (
    <>
      {blockConfirmUser && (
        <div className="feed-block-overlay" onClick={handleCancelBlock}>
          <div className="feed-block-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="feed-block-icon">
              <FaBan />
            </div>
            <h3 className="feed-block-title">
              {t.blockModal?.title || "Block this user?"}
            </h3>
            <p className="feed-block-subtitle">
              {t.blockModal?.subtitle
                ? t.blockModal.subtitle.replace('{{username}}', blockConfirmUser.username || '')
                : `You will no longer see posts from ${blockConfirmUser.username || 'this user'}.`}
            </p>
            {blockError && <div className="feed-block-error">{blockError}</div>}
            <div className="feed-block-actions">
              <button
                type="button"
                className="feed-block-button feed-block-secondary"
                onClick={handleCancelBlock}
                disabled={blockSubmitting}
              >
                {t.blockModal?.cancel || "Cancel"}
              </button>
              <button
                type="button"
                className="feed-block-button feed-block-primary"
                onClick={handleConfirmBlock}
                disabled={blockSubmitting}
              >
                {blockSubmitting
                  ? t.blockModal?.submitting || "Blocking..."
                  : t.blockModal?.confirm || "Block user"}
              </button>
            </div>
          </div>
        </div>
      )}

      {hideConfirmPost && (
        <div className="feed-hide-overlay" onClick={() => setHideConfirmPost(null)}>
          <div className="feed-hide-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="feed-hide-icon">
              <FaEyeSlash />
            </div>
            <h3 className="feed-hide-title">{t.hideModal?.title || "Hide this post?"}</h3>
            <p className="feed-hide-subtitle">
              {t.hideModal?.subtitle || "Are you sure you no longer want to see this post?"}
            </p>
            <div className="feed-hide-actions">
              <button
                type="button"
                className="feed-hide-button feed-hide-secondary"
                onClick={() => setHideConfirmPost(null)}
              >
                {t.hideModal?.cancel || "Cancel"}
              </button>
              <button
                type="button"
                className="feed-hide-button feed-hide-primary"
                onClick={() => {
                  if (hideConfirmPost?.id) {
                    hidePostById(hideConfirmPost.id);
                  }
                  setHideConfirmPost(null);
                  handleCancelReport();
                }}
              >
                {t.hideModal?.confirm || "Hide post"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmPost && (
        <div className="feed-delete-overlay" onClick={handleCancelDelete}>
          <div className="feed-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="feed-delete-icon">
              <FaTrash />
            </div>
            <h3 className="feed-delete-title">
              {t.deleteModal?.title || "Delete this post?"}
            </h3>
            <p className="feed-delete-subtitle">
              {t.deleteModal?.subtitle || "Are you sure you want to delete this post?"}
            </p>
            {deleteError && <div className="feed-delete-error">{deleteError}</div>}
            <div className="feed-delete-actions">
              <button
                type="button"
                className="feed-delete-button feed-delete-secondary"
                onClick={handleCancelDelete}
                disabled={deleteSubmitting}
              >
                {t.deleteModal?.cancel || "Cancel"}
              </button>
              <button
                type="button"
                className="feed-delete-button feed-delete-primary"
                onClick={handleConfirmDelete}
                disabled={deleteSubmitting}
              >
                {deleteSubmitting
                  ? (t.deleteModal?.submitting || "Deleting...")
                  : (t.deleteModal?.confirm || "Delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      {reportPost && (
        <div className="feed-report-overlay" onClick={handleCancelReport}>
          <div className="feed-report-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="feed-report-header">
              <div className="feed-report-icon">
                <FaFlag />
              </div>
              <h3 className="feed-report-title">{t.reportModal?.title || "Report Post"}</h3>
              <p className="feed-report-subtitle">
                {t.reportModal?.subtitle || "Why are you reporting this post?"}
              </p>
            </div>
            <div className="feed-report-options">
              {REPORT_REASONS.map((reason) => (
                <button
                  type="button"
                  key={reason.value}
                  className={`feed-report-option ${selectedReportReason === reason.value ? "active" : ""}`}
                  onClick={() => setSelectedReportReason(reason.value)}
                >
                  <span className="feed-report-option-title">
                    {t.reportModal?.reasons?.[reason.value]?.title || reason.label}
                  </span>
                  <span className="feed-report-option-desc">
                    {t.reportModal?.reasons?.[reason.value]?.description || reason.description}
                  </span>
                </button>
              ))}
            </div>
            <textarea
              className="feed-report-textarea"
              placeholder={t.reportModal?.placeholder || "Add more details (optional)"}
              value={reportDescription}
              onChange={(e) => setReportDescription(e.target.value)}
              rows={3}
            />
            {reportError && <div className="feed-report-error">{reportError}</div>}
            <div className="feed-report-actions">
              <button
                type="button"
                className="feed-report-button feed-report-secondary"
                onClick={handleCancelReport}
              >
                {t.reportModal?.cancel || "Cancel"}
              </button>
              <button
                type="button"
                className="feed-report-button feed-report-primary"
                onClick={handleSubmitReport}
                disabled={!selectedReportReason || reportSubmitting}
              >
                {reportSubmitting
                  ? t.reportModal?.submitting || "Submitting..."
                  : t.reportModal?.submit || "Submit report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLikeDetailsOpen && (
        <div className="profile-posts-like-details-overlay" onClick={closeLikeDetails}>
          <div className="profile-posts-like-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="profile-posts-like-details-header">
              <div className="profile-posts-like-details-heading-text">
                <h3>{t.likesOverlay?.title || "Post Likes"}</h3>
                {likeDetailsPost?.caption && (
                  <p className="profile-posts-like-details-subtitle">
                    {likeDetailsPost.caption.length > 80
                      ? `${likeDetailsPost.caption.slice(0, 77)}…`
                      : likeDetailsPost.caption}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="profile-posts-like-details-close"
                onClick={closeLikeDetails}
                aria-label={t.likesOverlay?.close || "Close"}
              >
                ×
              </button>
            </div>
            <div className="profile-posts-like-details-content">
              {likeDetailsLoading ? (
                <div className="profile-posts-like-details-status">
                  {t.likesOverlay?.loading || "Loading likes…"}
                </div>
              ) : likeDetailsError ? (
                <div className="profile-posts-like-details-status error">
                  {likeDetailsError}
                </div>
              ) : likeDetailsUsers.length === 0 ? (
                <div className="profile-posts-like-details-status">
                  {t.likesOverlay?.empty || "No likes yet."}
                </div>
              ) : (
                <div className="profile-posts-like-details-list">
                  {likeDetailsUsers.map((likedUser) => (
                    <div key={likedUser.id} className="profile-posts-like-details-item">
                      <div className="profile-posts-like-user">
                        <img
                          src={likedUser.avatar || getDefaultUserIcon()}
                          alt={likedUser.username}
                          onError={(e) => { e.target.src = getDefaultUserIcon(); }}
                        />
                        <span>{likedUser.username}</span>
                      </div>
                      {likedUser.id !== authUserId && authUserId && (
                        <button
                          type="button"
                          className={`profile-posts-like-details-follow-btn ${likedUser.isFollowed ? "unfollow" : ""}`}
                          onClick={() => toggleFollowUser(likedUser.id, likedUser.isFollowed)}
                          disabled={!!followLoadingMap[likedUser.id]}
                        >
                          {followLoadingMap[likedUser.id]
                            ? "…"
                            : likedUser.isFollowed
                              ? (t.likesOverlay?.unfollow || "Unfollow")
                              : (t.likesOverlay?.follow || "Follow")}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="profile-posts-page two-col">
      <button 
        className="pages-back-btn" 
        onClick={() => navigate(-1)}
        title="Go back"
      >
        <FaArrowLeft />
      </button>
      
      <FloatingSidebarButton userProfile={{ name: pageUser?.username, profileImage: pageUser?.profileImage }} />

      <div className="profile-posts-layout">
        {/* LEFT: Main selected post */}
        <div className="main-post-column">
          {selectedPost ? (
            <PostItem
              key={selectedPost.id}
              post={selectedPost}
              pageUser={pageUser}
              tab={tab}
              isMenuOpen={openMenu === selectedPost.id}
              toggleMenu={() => setOpenMenu(openMenu === selectedPost.id ? null : selectedPost.id)}
              onCloseMenu={() => setOpenMenu(null)}
              isLiked={likedPosts.has(selectedPost.id)}
              isSaved={savedPosts.has(selectedPost.id)}
              onLike={() => handleLike(selectedPost.id)}
              onSave={() => handleSave(selectedPost.id)}
              onUserClick={handleUserClick}
              variant="main"
              onOpenFullscreen={openFullscreen}
              onOpenComments={() => openCommentsOverlay(selectedPost.id)}
              commentCount={selectedPost.comment_count || 0}
              onShare={sharePost}
              onHideRequest={handleShowHideModal}
              onReportRequest={handleShowReportModal}
              onBlockRequest={handleShowBlockModal}
              isOwner={isViewingOwnProfile && (selectedPost.user_id === authUserId || selectedPost.postedById === authUserId)}
              onEditRequest={handleEditPostRequest}
              onDeleteRequest={handleDeletePostRequest}
              onInsightsRequest={handleInsightsRequest}
              onToggleCommentsRequest={handleToggleCommentsRequest}
              onToggleRepostsRequest={handleToggleRepostsRequest}
              onShowLikeDetails={openLikeDetails}
            />
          ) : (
            <div className="empty-main">{t?.noPostSelected || "No post selected."}</div>
          )}
        </div>

        {/* RIGHT: Other posts */}
        <aside className="sidebar-posts-column">
          <div className="sidebar-header">
          <span>
  {(t.moreFrom ? t.moreFrom.replace("{{username}}", pageUser?.username || "") : "More from {{username}}")
    .replace("{{username}}", pageUser?.username || "")}
</span>

          </div>
          <div className="sidebar-scroll">
            <div className="sidebar-tiles">
              {sidePosts.map(p => (
                <PostTile
                  key={p.id}
                  post={p}
                  isActive={p.id === selectedPostId}
                  onClick={() => setSelectedPostId(p.id)}
                  isLiked={likedPosts.has(p.id)}
                  isSaved={savedPosts.has(p.id)}
                  onLike={() => handleLike(p.id)}
                  onSave={() => handleSave(p.id)}
                  onOpenComments={() => openCommentsOverlay(p.id)}
                  onShare={sharePost}
                />
              ))}
              {sidePosts.length === 0 && <div className="no-more-posts">{t?.noMorePosts || "No more posts"}</div>}
            </div>
          </div>
        </aside>
      </div>

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div className="profile-posts-fullscreen-modal" onClick={closeFullscreen}>
          <div className="profile-posts-fullscreen-content">
            <button className="profile-posts-fullscreen-close" onClick={closeFullscreen}>
              ×
            </button>
            <div className="profile-posts-fullscreen-frame" style={{ '--media-url': `url(${fullscreenImage})` }}>
              <img 
                src={fullscreenImage} 
                alt="Fullscreen" 
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}

      {commentsOverlayPostId && (
        <CommentsOverlay
          post={posts.find(p => p.id === commentsOverlayPostId) || null}
          comments={commentsData}
          setComments={setCommentsData}
          isLoading={commentsLoading}
          error={commentsError}
          onClose={closeCommentsOverlay}
          onRetry={() => commentsOverlayPostId && fetchComments(commentsOverlayPostId)}
          t={t}
          currentUserId={authUserId}
          onAdjustPostCommentCount={adjustPostCommentCount}
        />
      )}
      {editPost && (
        <EditPostOverlay
          post={editPost}
          onClose={handleCloseEdit}
          onPostUpdated={handlePostUpdated}
          authUserId={authUserId}
          t={t}
          ownerProfile={pageUser}
        />
      )}
    </div>
    </>
  );
}

function PostTile({ post, isActive, onClick, isLiked, isSaved, onLike, onSave, onOpenComments, onShare }) {
  const { translations: tRaw } = useLanguage();
  const t = tRaw?.profilePosts || {};
  const raw = Array.isArray(post.media) && post.media.length
    ? post.media
    : Array.isArray(post.compressedMedia)
    ? post.compressedMedia
    : [];
  const entries = raw.map(r => (typeof r === "string" ? JSON.parse(r) : r)).filter(Boolean);
  const preview = entries[0];

  const isLocked = post.isLocked === true;
  const commentsEnabled = post.is_comments_enabled !== false;

  const handleLike = (e) => {
    e.stopPropagation();
    onLike();
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    await onShare?.(post);
  };

  const handleSave = (e) => {
    e.stopPropagation();
    onSave();
  };

  const handleComments = (e) => {
    e.stopPropagation();
    onOpenComments?.();
  };

  return (
    <button
      className={`post-tile ${isActive ? "active" : ""} ${isLocked ? "is-locked" : ""}`}
      onClick={onClick}
      title={t.tooltips?.openPost || "Open post"}

    >
      {preview?.type === "image" ? (
        <img src={preview.url} alt="" />
      ) : preview?.type === "video" ? (
        <video src={preview.url} muted />
      ) : null}

      <div className="tile-stats" aria-hidden="true">
        <span className="tile-stat">
          <FaHeart />
          <span>{Math.max(0, post.like_count || 0)}</span>
        </span>
        <span className="tile-stat">
          <FaComment />
          <span>{Math.max(0, post.comment_count || 0)}</span>
        </span>
      </div>

      {isLocked && <span className="tile-locked-dim" aria-hidden="true" />}
      {isLocked && (
        <span className="profile-posts-locked" aria-label={t.tooltips?.locked || "Locked"} title={t.tooltips?.locked || "Locked"}>
          <svg viewBox="0 0 24 24">
            <path d="M12 1C8.14 1 5 4.14 5 8v1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V10c0-.55-.45-1-1-1h-1V8c0-3.86-3.14-7-7-7zm0 2c2.76 0 5 2.24 5 5v1H7V8c0-2.76 2.24-5 5-5zm0 10c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"/>
          </svg>
        </span>
      )}
      <span className="tile-overlay" aria-hidden="true" />
      {!isLocked && (
        <div className="tile-actions">
          <button
            className={`tile-btn like ${isLiked ? "is-liked" : ""}`}
            aria-label={isLiked ? "Unlike" : "Like"}
            aria-pressed={isLiked}
            onClick={handleLike}
          >
            <FaHeart />
          </button>
          <button
            className="tile-btn share"
            aria-label="Share"
            onClick={handleShare}
          >
            <FaShareAlt />
          </button>
          <button
            className={`tile-btn save ${isSaved ? "is-saved" : ""}`}
            aria-label={isSaved ? "Unsave" : "Save"}
            aria-pressed={isSaved}
            onClick={handleSave}
          >
            <FaBookmark />
          </button>
          {commentsEnabled && (
            <button
              className="tile-btn comment"
              aria-label={t.tooltips?.viewComments || "View comments"}
              onClick={handleComments}
            >
              <FaComment />
            </button>
          )}
        </div>
      )}
    </button>
  );
}


function PostItem({
  post,
  pageUser,
  tab,
  isMenuOpen,
  toggleMenu,
  onCloseMenu,
  isLiked,
  isSaved,
  onLike,
  onSave,
  onUserClick,
  variant = "main",
  onOpenFullscreen,
  onOpenComments,
  commentCount = 0,
  onShare,
  onHideRequest,
  onReportRequest,
  onBlockRequest,
  isOwner = false,
  onEditRequest,
  onDeleteRequest,
  onInsightsRequest,
  onToggleCommentsRequest,
  onToggleRepostsRequest,
  onShowLikeDetails
}) {
  const { translations: tRaw } = useLanguage();
  const t = tRaw?.profilePosts || {};

  const raw =
    Array.isArray(post.media) && post.media.length
      ? post.media
      : Array.isArray(post.compressedMedia)
      ? post.compressedMedia
      : [];
  const entries = raw.map(r => (typeof r === "string" ? JSON.parse(r) : r)).filter(Boolean);

  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [post.id]);
  const mediaRef = useRef(null);

  // click-outside refs
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handlePointerDown = (e) => {
      const menuEl = menuRef.current;
      const btnEl = menuBtnRef.current;
      if (!menuEl || !btnEl) return;
      const target = e.target;
      const clickedInsideMenu = menuEl.contains(target);
      const clickedMenuButton = btnEl.contains(target);
      if (!clickedInsideMenu && !clickedMenuButton) onCloseMenu?.();
    };
    const handleKey = (e) => { if (e.key === "Escape") onCloseMenu?.(); };
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isMenuOpen, onCloseMenu]);

  if (!entries.length) return null;

  const isRepost = tab === "reposts";
  const isLocked = post.isLocked === true;

  const authorAvatar = isRepost ? post.postedByProfileImg : pageUser.profileImage;
  const authorName = isRepost ? post.postedByUsername : pageUser.username;

  const commentsEnabled = post.is_comments_enabled !== false;
  const repostsEnabled = post.is_reposts_enabled !== false;

  const commentToggleLabel = commentsEnabled
    ? (t.menu?.turnOffComments || "Turn off comments")
    : (t.menu?.turnOnComments || "Turn on comments");

  const commentToggleIcon = commentsEnabled ? (
    <FaCommentSlash />
  ) : (
    <FaComment />
  );

  const repostToggleLabel = repostsEnabled
    ? (t.menu?.turnOffReposts || "Turn off reposts")
    : (t.menu?.turnOnReposts || "Turn on reposts");

  const repostToggleIcon = repostsEnabled ? (
    <span className="dropdown-icon-with-slash" aria-hidden="true">
      <FaRetweet />
      <FaSlash className="dropdown-icon-slash" />
    </span>
  ) : (
    <FaRetweet />
  );

  const next = e => { e.stopPropagation(); setIdx(i => (i + 1) % entries.length); };
  const prev = e => { e.stopPropagation(); setIdx(i => (i - 1 + entries.length) % entries.length); };

  const handleExpand = (e) => {
    e.stopPropagation();
    if (onOpenFullscreen && entries[idx]?.url) {
      onOpenFullscreen(entries[idx].url);
    }
  };

  // --- detect if current media is a video
  const isVideo = entries[idx]?.type === "video";

  // --- caption with link on separate line
  const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/i;
  const normalizeUrl = (u) => (u?.startsWith("http") ? u : `https://${u}`);

  const renderCaptionBlock = (rawText) => {
    if (!rawText) return null;

    const match = String(rawText).match(URL_RE);
    const firstUrl = match ? match[0] : null;

    // caption = text without the first url, flatten newlines
    const caption = String(rawText)
      .replace(URL_RE, "")
      .replace(/\s*\r?\n+\s*/g, " ")
      .trim();

    return (
      <div className="profile-post-caption">
        {caption && (
          <p className="profile-post-caption-line" dir="auto">
            {caption}
          </p>
        )}
        {firstUrl && (
          <p className="profile-post-caption-line caption-link-line">
            <a
              href={normalizeUrl(firstUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="caption-link"
              onClick={(e) => e.stopPropagation()}
            >
              {firstUrl}
            </a>
          </p>
        )}
      </div>
    );
  };

  return (
    <div id={post.id} className={`post-container ${variant === "main" ? "post-main" : ""}`}>
      {isRepost && (
        <div className="repost-badge">
          <img 
            src={pageUser.profileImage || getDefaultUserIcon()} 
            alt={pageUser.username} 
            className="repost-avatar"
            onError={(e) => {
              e.target.src = getDefaultUserIcon();
            }}
          />
        </div>
      )}

      {/* Top header with back arrow and menu - stays at top */}
      <div className="post-top-header">
        <div className="post-header-actions">
          {isRepost && <FaRetweet className="post-action-icon" />}
          <div className="menu-wrapper" ref={menuRef}>
            <button
              ref={menuBtnRef}
              className="posts-menu-button"
              onClick={(e) => { e.stopPropagation(); toggleMenu(); }}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
            >
              <FaEllipsisV />
            </button>
            {isMenuOpen && (
              <div
                className="posts-dropdown-menu"
                role="menu"
                onClick={(e) => e.stopPropagation()}
              >
                {isOwner ? (
                  <>
                    <div
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        onEditRequest?.(post);
                        onCloseMenu?.();
                      }}
                    >
                      <FaEdit /> {t.menu?.editPost || "Edit post"}
                    </div>
                    <div
                      className="dropdown-item danger"
                      role="menuitem"
                      onClick={() => {
                        onDeleteRequest?.(post);
                        onCloseMenu?.();
                      }}
                    >
                      <FaTrash /> {t.menu?.deletePost || "Delete post"}
                    </div>
                    <div
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        onInsightsRequest?.(post);
                        onCloseMenu?.();
                      }}
                    >
                      <FaChartLine /> {t.menu?.insights || "Insights"}
                    </div>
                    <div
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        onToggleCommentsRequest?.(post);
                        onCloseMenu?.();
                      }}
                    >
                      {commentToggleIcon} {commentToggleLabel}
                    </div>
                    <div
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        onToggleRepostsRequest?.(post);
                        onCloseMenu?.();
                      }}
                    >
                      {repostToggleIcon} {repostToggleLabel}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        onHideRequest?.(post);
                        onCloseMenu?.();
                      }}
                    >
                      <FaEyeSlash /> {t.menu?.notInterested || "Not Interested"}
                    </div>
                    <div
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        onBlockRequest?.(post);
                        onCloseMenu?.();
                      }}
                    >
                      <FaBan /> {t.menu?.blockUser || "Block User"}
                    </div>
                    <div
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => {
                        onReportRequest?.(post);
                        onCloseMenu?.();
                      }}
                    >
                      <FaFlag /> {t.menu?.reportPost || "Report Post"}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={`post-media ${isLocked ? "locked" : ""}`}>
        <div className="post-media-frame" style={{ '--media-url': isLocked ? 'none' : `url(${entries[idx].url})` }}>
          {/* Hide carousel arrows when locked */}
          {!isLocked && entries.length > 1 && (
            <button className="carousel-arrow left" onClick={prev}>‹</button>
          )}

          {/* Hide media when locked */}
          {!isLocked && (
            <>
              {entries[idx].type === "image" ? (
                <img ref={mediaRef} src={entries[idx].url} alt="" />
              ) : (
                <video ref={mediaRef} controls src={entries[idx].url} />
              )}
            </>
          )}

          {/* Hide carousel arrows when locked */}
          {!isLocked && entries.length > 1 && (
            <button className="carousel-arrow right" onClick={next}>›</button>
          )}

          {/* Hide expand button when locked or if it's a video */}
          {!isLocked && !isVideo && (
            <button className="post-media-expand" onClick={handleExpand} title="Expand">
              <FaExpand />
            </button>
          )}

          {/* Show lock overlay when locked */}
          {isLocked && (
            <div className="locked-overlay">
              <div className="lock-icon-centered">
                <svg className="lock-icon" viewBox="0 0 24 24">
                  <path d="M12 1C8.14 1 5 4.14 5 8v1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V10c0-.55-.45-1-1-1h-1V8c0-3.86-3.14-7-7-7zm0 2c2.76 0 5 2.24 5 5v1H7V8c0-2.76 2.24-5 5-5zm0 10c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"/>
                </svg>
              </div>
            </div>
          )}
          
          {/* Render audio player inside the frame at the bottom - hide when locked */}
          {!isLocked && (post.audioUrl || post.audio_filename) && (
            <div className="post-audio-container">
              <audio
                controls
                src={post.audioUrl || `${POST_MEDIA_BASE_URL}/${post.user_id}/${post.audio_filename}`}
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </div>

      {/* User profile info below media */}
      <div className="post-user-section">
        <div className="post-user-info">
          <img 
            src={authorAvatar || getDefaultUserIcon()} 
            alt="" 
            className="post-avatar"
            style={{ cursor: 'pointer' }}
            onClick={() => onUserClick(isRepost ? post.postedById : pageUser.id)}
            onError={(e) => {
              e.target.src = getDefaultUserIcon();
            }}
          />
          <span 
            className="post-username"
            style={{ cursor: 'pointer' }}
            onClick={() => onUserClick(isRepost ? post.postedById : pageUser.id)}
          >
            {authorName}
          </span>
        </div>
      </div>

      {/* Caption below user info - hide when locked */}
      {post.caption && !isLocked && renderCaptionBlock(post.caption)}

      {/* Hide post actions when locked */}
      {!isLocked && (
        <div className="post-actions">
          <div
            className={`post-action-group ${isLiked ? 'liked' : ''}`}
            onClick={(e) => {
            if (!onShowLikeDetails) return;
              e.stopPropagation();
              onShowLikeDetails(post);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
            if (!onShowLikeDetails) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onShowLikeDetails(post);
              }
            }}
          >
            <button
              type="button"
              className={`post-like-icon-button ${isLiked ? 'liked' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                onLike();
              }}
              aria-label={isLiked ? (t.menu?.unlike || "Unlike") : (t.menu?.like || "Like")}
            >
              <FaHeart className={`post-icon heart ${isLiked ? 'liked' : ''}`} />
            </button>
            <span className="post-action-count">{Math.max(0, post.like_count || 0)}</span>
          </div>
          {commentsEnabled && (
            <button
              type="button"
              className="post-action-group"
              onClick={onOpenComments}
            >
              <FaComment className="post-icon" />
              <span className="post-action-count">{Math.max(0, commentCount)}</span>
            </button>
          )}
          <FaShareAlt
            className="post-icon"
            onClick={() => onShare?.(post)}
          />
          {!isOwner && <FaGift className="post-icon" />}
          <FaBookmark
            className={`post-icon ${isSaved ? 'saved' : ''}`}
            onClick={onSave}
            style={{ cursor: 'pointer' }}
          />
        </div>
      )}
    </div>
  );
}


const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffSeconds = Math.round(diffMs / 1000);
  if (diffSeconds < 60) return 'Just now';

  const diffMinutes = Math.round(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  const diffWeeks = Math.round(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;

  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;

  const diffYears = Math.round(diffDays / 365);
  return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
};


function CommentsOverlay({
  post,
  comments,
  setComments,
  isLoading,
  error,
  onClose,
  onRetry,
  t,
  currentUserId,
  onAdjustPostCommentCount
}) {
  const fallbackTitle = t?.commentsTitle || 'Comments';
  const [draft, setDraft] = useState('');
  const [replyTarget, setReplyTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const inputRef = useRef(null);

  const structuredComments = useMemo(() => {
    if (!Array.isArray(comments) || comments.length === 0) return [];

    const replyBuckets = new Map();
    comments.forEach(comment => {
      if (!comment?.parent_comment_id) return;
      const bucket = replyBuckets.get(comment.parent_comment_id) || [];
      bucket.push(comment);
      replyBuckets.set(comment.parent_comment_id, bucket);
    });

    const sortByDate = (a, b) => {
      const aTime = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const bTime = b?.created_at ? new Date(b.created_at).getTime() : 0;
      return aTime - bTime;
    };

    return comments
      .filter(comment => !comment.parent_comment_id)
      .sort(sortByDate)
      .map(comment => ({
        ...comment,
        replies: (replyBuckets.get(comment.id) || []).sort(sortByDate)
      }));
  }, [comments]);

  useEffect(() => {
    setDraft('');
    setReplyTarget(null);
    setFormError(null);
  }, [post?.id]);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const handleReply = useCallback((comment) => {
    setReplyTarget(comment);
    setFormError(null);
    focusInput();
  }, [focusInput]);

  const clearReplyTarget = useCallback(() => {
    setReplyTarget(null);
  }, []);

  const handleToggleCommentLike = useCallback(async (commentId) => {
    if (!currentUserId) {
      setFormError(t?.authRequired || 'Sign in to interact with comments.');
      return;
    }

    const target = comments.find(c => c.id === commentId);
    if (!target) return;

    const currentlyLiked = target.isLikedByCurrentUser;
    const delta = currentlyLiked ? -1 : 1;

    setFormError(null);
    setComments(prev => prev.map(c => {
      if (c.id !== commentId) return c;
      return {
        ...c,
        likeCount: Math.max(0, (c.likeCount || 0) + delta),
        isLikedByCurrentUser: !currentlyLiked
      };
    }));

    try {
      if (currentlyLiked) {
        const { error: unlikeError } = await supabase
          .from('comment_likes')
          .delete()
          .eq('user_id', currentUserId)
          .eq('comment_id', commentId);
        if (unlikeError) throw unlikeError;
      } else {
        const { error: likeError } = await supabase
          .from('comment_likes')
          .insert({
            user_id: currentUserId,
            comment_id: commentId,
            created_at: new Date().toISOString()
          });
        if (likeError) throw likeError;
      }
    } catch (toggleError) {
      console.error('[ProfilePosts] Error toggling comment like:', toggleError);
      setComments(prev => prev.map(c => {
        if (c.id !== commentId) return c;
        return {
          ...c,
          likeCount: Math.max(0, (c.likeCount || 0) - delta),
          isLikedByCurrentUser: currentlyLiked
        };
      }));
      setFormError(toggleError?.message || t?.commentLikeError || 'Unable to update like.');
    }
  }, [comments, currentUserId, setComments, t]);

  const handleSubmit = useCallback(async (event) => {
    if (event) event.preventDefault();
    if (!currentUserId) {
      setFormError(t?.authRequired || 'Sign in to add a comment.');
      return;
    }

    const trimmed = draft.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setFormError(null);

    try {
      const payload = {
        post_id: post.id,
        user_id: currentUserId,
        content: trimmed,
        created_at: new Date().toISOString()
      };

      if (replyTarget?.id) {
        payload.parent_comment_id = replyTarget.id;
      }

      const { error: insertError } = await supabase
        .from('comments')
        .insert(payload);

      if (insertError) throw insertError;

      onAdjustPostCommentCount?.(post.id, 1);
      setDraft('');
      clearReplyTarget();
      focusInput();
      await onRetry?.();
    } catch (submitError) {
      console.error('[ProfilePosts] Error adding comment:', submitError);
      setFormError(submitError?.message || t?.commentSubmitError || 'Could not add comment.');
    } finally {
      setSubmitting(false);
    }
  }, [clearReplyTarget, currentUserId, draft, focusInput, onAdjustPostCommentCount, onRetry, post?.id, replyTarget?.id, t]);

  const handleClearDraft = useCallback(() => {
    setDraft('');
    setFormError(null);
  }, []);

  return (
    <div className="profile-posts-comments-modal" onClick={onClose}>
      <div className="profile-posts-comments-content" onClick={(e) => e.stopPropagation()}>
        <div className="comments-header">
          <div className="comments-post-meta">
            <div className="comments-post-text">
              <span className="comments-post-title">{fallbackTitle}</span>
            </div>
          </div>
          <button type="button" className="comments-close" onClick={onClose} aria-label={t?.close || 'Close'}>
            ×
          </button>
        </div>
        <div className="comments-body">
          {isLoading ? (
            <div className="comments-status">{t?.loadingComments || 'Loading comments…'}</div>
          ) : error ? (
            <div className="comments-status comments-error">
              <p>{error}</p>
              <button type="button" onClick={onRetry} className="comments-retry">
                {t?.retry || 'Retry'}
              </button>
            </div>
          ) : structuredComments.length === 0 ? (
            <div className="comments-status">{t?.noCommentsYet || 'No comments yet.'}</div>
          ) : (
            <ul className="comments-list">
              {structuredComments.map(comment => {
                const replyCount = comment.replies?.length || 0;
                return (
                  <li key={comment.id} className={`comments-item ${replyTarget?.id === comment.id ? 'is-reply-target' : ''}`}>
                    <img
                      src={comment.author?.avatar || getDefaultUserIcon()}
                      alt={comment.author?.displayName || comment.author?.username || 'Comment author'}
                      className="comments-item-avatar"
                      onError={(e) => {
                        e.target.src = getDefaultUserIcon();
                      }}
                    />
                    <div className="comments-item-card">
                      <div className="comments-item-meta">
                        <span className="comments-item-author">
                          {comment.author?.displayName || comment.author?.username || t?.unknownUser || 'Unknown User'}
                        </span>
                        <span className="comments-item-time">
                          {formatRelativeTime(comment.created_at)}
                        </span>
                      </div>
                      <p className="comments-item-text" dir="auto">{comment.content}</p>
                      <div className="comments-item-actions">
                        <button
                          type="button"
                          className={`comment-action like ${comment.isLikedByCurrentUser ? 'is-active' : ''}`}
                          onClick={() => handleToggleCommentLike(comment.id)}
                        >
                          {comment.isLikedByCurrentUser ? <FaHeart /> : <FaRegHeart />}
                          <span>{comment.likeCount || 0}</span>
                        </button>
                        <button
                          type="button"
                          className="comment-action comment"
                          onClick={() => handleReply(comment)}
                        >
                          <FaRegComment />
                          <span>{replyCount}</span>
                        </button>
                        <button
                          type="button"
                          className="comment-reply-link"
                          onClick={() => handleReply(comment)}
                        >
                          {t?.reply || 'Reply'}
                        </button>
                      </div>

                      {replyCount > 0 && (
                        <div className="comment-replies">
                          {comment.replies.map(reply => (
                            <div
                              key={reply.id}
                              className={`comment-reply ${replyTarget?.id === reply.id ? 'is-reply-target' : ''}`}
                            >
                              <img
                                src={reply.author?.avatar || getDefaultUserIcon()}
                                alt={reply.author?.displayName || reply.author?.username || 'Reply author'}
                                className="comment-reply-avatar"
                                onError={(e) => {
                                  e.target.src = getDefaultUserIcon();
                                }}
                              />
                              <div className="comment-reply-card">
                                <div className="comment-reply-meta">
                                  <span className="comment-reply-author">
                                    {reply.author?.displayName || reply.author?.username || t?.unknownUser || 'Unknown User'}
                                  </span>
                                  <span className="comment-reply-time">{formatRelativeTime(reply.created_at)}</span>
                                </div>
                                <p className="comment-reply-text" dir="auto">{reply.content}</p>
                                <div className="comment-reply-actions">
                                  <button
                                    type="button"
                                    className={`comment-action like ${reply.isLikedByCurrentUser ? 'is-active' : ''}`}
                                    onClick={() => handleToggleCommentLike(reply.id)}
                                  >
                                    {reply.isLikedByCurrentUser ? <FaHeart /> : <FaRegHeart />}
                                    <span>{reply.likeCount || 0}</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="comment-reply-link"
                                    onClick={() => handleReply(reply)}
                                  >
                                    {t?.reply || 'Reply'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <form className="comments-form" onSubmit={handleSubmit}>
            {replyTarget && (
              <div className="comments-reply-indicator">
                <span>{t?.replyingTo || 'Replying to'} {replyTarget.author?.displayName || replyTarget.author?.username || 'User'}</span>
                <button type="button" onClick={clearReplyTarget} aria-label={t?.cancelReply || 'Cancel reply'}>
                  <FaTimes />
                </button>
              </div>
            )}
            {formError && <p className="comments-form-error">{formError}</p>}
            <div className="comments-input-row">
              <textarea
                ref={inputRef}
                className="comments-input"
                placeholder={replyTarget ? (t?.replyPlaceholder || 'Write a reply…') : (t?.addCommentPlaceholder || 'Add a comment…')}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={1}
                disabled={submitting}
              />
              <div className="comments-input-actions">
                <button
                  type="button"
                  className="comments-clear"
                  onClick={handleClearDraft}
                  disabled={submitting || (!draft && !replyTarget)}
                  aria-label={t?.clear || 'Clear'}
                >
                  <FaTimes />
                </button>
                <button
                  type="submit"
                  className="comments-send"
                  disabled={submitting || !draft.trim()}
                  aria-label={t?.send || 'Send'}
                >
                  <FaPaperPlane />
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function EditPostOverlay({ post, onClose, onPostUpdated, authUserId, t, ownerProfile }) {
  const [caption, setCaption] = useState(post?.caption || "");
  const [existingMedia, setExistingMedia] = useState(() => {
    const filenames = Array.isArray(post?.media_names) ? post.media_names : [];
    return (Array.isArray(post?.media) ? post.media : filenames.map((filename) => ({
      url: `${POST_MEDIA_BASE_URL}/${post.user_id}/${filename}`,
      type: /\.(mp4|mov|webm)$/i.test(filename) ? "video" : "image",
    }))).map((item, idx) => ({
      filename: filenames[idx] || item.filename || null,
      url: item.url,
      type: item.type || ((filenames[idx] || "").match(/\.(mp4|mov|webm)$/i) ? "video" : "image"),
    })).filter((item) => item.filename);
  });
  const [existingAudio, setExistingAudio] = useState(() => {
    if (!post?.audio_filename) return null;
    return {
      filename: post.audio_filename,
      url: `${POST_MEDIA_BASE_URL}/${post.user_id}/${post.audio_filename}`,
    };
  });
  const [newMedia, setNewMedia] = useState([]);
  const [newAudio, setNewAudio] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const audioInputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    return () => {
      newMedia.forEach((item) => {
        if (item?.url && item.url.startsWith("blob:")) {
          URL.revokeObjectURL(item.url);
        }
      });
      if (newAudio?.url && newAudio.url.startsWith("blob:")) {
        URL.revokeObjectURL(newAudio.url);
      }
      if (mediaStream) {
        try {
          mediaStream.getTracks().forEach((track) => track.stop());
        } catch (err) {
          console.warn("[ProfilePosts] Failed to stop media stream:", err);
        }
      }
    };
  }, [mediaStream, newAudio, newMedia]);

  const buildMediaUrl = useCallback(
    (filename) => `${POST_MEDIA_BASE_URL}/${post.user_id}/${filename}`,
    [post.user_id]
  );

  const getExtension = (file) => {
    const fromName = (file.name || "").split(".").pop();
    const fromType = (file.type || "").split("/").pop();
    let ext = (fromName || fromType || "bin").toLowerCase();
    if (ext === "jpeg") ext = "jpg";
    if (ext === "quicktime") ext = "mov";
    if (ext === "x-m4a") ext = "m4a";
    return ext;
  };

  const appendNewMedia = (files, fallbackType = "image") => {
    if (!files.length) return;
    const mapped = files.map((file) => {
      const type = file.type?.startsWith("video/")
        ? "video"
        : file.type?.startsWith("image/")
        ? "image"
        : fallbackType;
      return {
        file,
        type,
        url: URL.createObjectURL(file),
      };
    });
    setNewMedia((prev) => [...prev, ...mapped]);
  };

  const handleMediaInputChange = (event, type) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    appendNewMedia(files, type);
    event.target.value = "";
  };

  const handleRemoveExistingMedia = (filename) => {
    setExistingMedia((prev) => prev.filter((item) => item.filename !== filename));
  };

  const handleRemoveExistingAudio = () => {
    setExistingAudio(null);
  };

  const handleRemoveNewMedia = (index) => {
    setNewMedia((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.url && removed.url.startsWith("blob:")) {
        URL.revokeObjectURL(removed.url);
      }
      return next;
    });
  };

  const handleRemoveNewAudio = () => {
    if (newAudio?.url && newAudio.url.startsWith("blob:")) {
      URL.revokeObjectURL(newAudio.url);
    }
    setNewAudio(null);
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorder) {
      try {
        mediaRecorder.stop();
      } catch (err) {
        console.warn("[ProfilePosts] Failed to stop recorder:", err);
      }
    }
    if (mediaStream) {
      try {
        mediaStream.getTracks().forEach((track) => track.stop());
      } catch (err) {
        console.warn("[ProfilePosts] Failed to stop media stream:", err);
      }
    }
    setMediaRecorder(null);
    setMediaStream(null);
    setRecording(false);
  }, [mediaRecorder, mediaStream]);

  const handleVoiceClick = async () => {
    if (recording) {
      stopRecording();
      return;
    }

    const getSupportedMimeType = () => {
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      for (const type of candidates) {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported(type)) return type;
      }
      return "";
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 48000,
        },
      });

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 128000,
      });

      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = () => {
        try {
          const blob = new Blob(chunks, { type: mimeType || "audio/webm" });
          const ext =
            (mimeType.includes("ogg") && "ogg") ||
            (mimeType.includes("mp4") && "m4a") ||
            "webm";
          const fileName = `audio_${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
          const file = new File([blob], fileName, { type: blob.type || `audio/${ext}` });

          if (existingAudio) {
            setExistingAudio(null);
          }
          handleRemoveNewAudio();

          const url = URL.createObjectURL(blob);
          setNewAudio({ file, url });
        } finally {
          try {
            stream.getTracks().forEach((track) => track.stop());
          } catch (err) {
            console.warn("[ProfilePosts] Failed to stop recording tracks:", err);
          }
          setMediaStream(null);
        }
      };

      recorder.start(250);
      setMediaRecorder(recorder);
      setMediaStream(stream);
      setRecording(true);
    } catch (err) {
      console.error("[ProfilePosts] Voice recording failed:", err);
      setError("Microphone access denied.");
    }
  };

  const handleAudioFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const file = files[0];
    if (existingAudio) {
      setExistingAudio(null);
    }
    handleRemoveNewAudio();
    setNewAudio({
      file,
      url: URL.createObjectURL(file),
    });
    event.target.value = "";
  };

  const totalMediaCount =
    existingMedia.length + (existingAudio ? 1 : 0) + newMedia.length + (newAudio ? 1 : 0);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!authUserId || authUserId !== post.user_id) {
      setError("You can only edit your own posts.");
      return;
    }

    const trimmedCaption = caption.trim();
    const mediaToKeep = existingMedia.map((item) => item.filename).filter(Boolean);
    let audioFilename = existingAudio?.filename || null;

    if (!trimmedCaption && mediaToKeep.length === 0 && newMedia.length === 0 && !existingAudio && !newAudio) {
      setError("Add a caption, media, or audio before saving.");
      return;
    }

    const prefix = Date.now().toString();
    let mediaIndex = mediaToKeep.length;

    setSubmitting(true);
    setError("");

    try {
      const newlyUploaded = [];

      for (const item of newMedia) {
        const ext = getExtension(item.file);
        const filename = `${prefix}_${mediaIndex}.${ext}`;
        const path = `${post.user_id}/${filename}`;
        const { error: uploadError } = await supabase.storage
          .from("post-media")
          .upload(path, item.file, {
            cacheControl: "3600",
            upsert: false,
            contentType: item.file.type || undefined,
          });
        if (uploadError) throw uploadError;
        newlyUploaded.push(filename);
        mediaIndex += 1;
      }

      if (newAudio) {
        const ext = getExtension(newAudio.file);
        const filename = `${prefix}_audio.${ext}`;
        const path = `${post.user_id}/${filename}`;
        const { error: audioUploadError } = await supabase.storage
          .from("post-media")
          .upload(path, newAudio.file, {
            cacheControl: "3600",
            upsert: false,
            contentType: newAudio.file.type || undefined,
          });
        if (audioUploadError) throw audioUploadError;
        audioFilename = filename;
      } else if (!existingAudio) {
        audioFilename = null;
      }

      const mediaNames = [...mediaToKeep, ...newlyUploaded];

      const updates = {
        caption: trimmedCaption,
        media_names: mediaNames,
        audio_filename: audioFilename,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from("posts")
        .update(updates)
        .eq("id", post.id);

      if (updateError) throw updateError;

      const updatedMediaEntries = mediaNames.map((filename) => ({
        filename,
        type: /\.(mp4|mov|webm)$/i.test(filename) ? "video" : "image",
        url: buildMediaUrl(filename),
      }));

      onPostUpdated?.({
        id: post.id,
        caption: trimmedCaption,
        media_names: mediaNames,
        media: updatedMediaEntries,
        audio_filename: audioFilename,
      });

      onClose();
    } catch (err) {
      console.error("[ProfilePosts] Failed to update post:", err);
      setError(err?.message || "Unable to save changes right now.");
    } finally {
      setSubmitting(false);
      stopRecording();
    }
  };

  const closeIfAllowed = () => {
    if (submitting) return;
    stopRecording();
    onClose();
  };

  return (
    <div className="profile-posts-edit-overlay" onClick={closeIfAllowed}>
      <div className="profile-posts-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-posts-edit-header">
          <h3>{t.editModal?.title || "Edit post"}</h3>
          <button
            type="button"
            className="profile-posts-edit-close"
            onClick={closeIfAllowed}
            aria-label={t.editModal?.close || "Close"}
          >
            <FaTimes />
          </button>
        </div>

        <div className="profile-posts-edit-body">
          <div className="profile-posts-edit-user">
            <img
              src={ownerProfile?.profileImage || resolveAvatarUrl(post.postedByProfileImg, post.user_id) || getDefaultUserIcon()}
              alt={ownerProfile?.username || post.postedByUsername || "User"}
              onError={(e) => {
                e.target.src = getDefaultUserIcon();
              }}
            />
            <span>@{ownerProfile?.username || post.postedByUsername || "you"}</span>
          </div>

          <label className="profile-posts-edit-label">
            {t.editModal?.captionLabel || "Caption"}
          </label>
          <textarea
            className="profile-posts-edit-caption"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={t.editModal?.captionPlaceholder || "Update your caption"}
          />

          <div className="profile-posts-edit-media-grid">
            {existingMedia.map((item) => (
              <div key={item.filename} className="profile-posts-edit-media-card">
                <button
                  type="button"
                  className="profile-posts-edit-remove"
                  onClick={() => handleRemoveExistingMedia(item.filename)}
                  aria-label={t.editModal?.removeMedia || "Remove media"}
                >
                  ×
                </button>
                {item.type === "video" ? (
                  <video src={item.url} controls />
                ) : (
                  <img src={item.url} alt="Post media" />
                )}
              </div>
            ))}

            {existingAudio && (
              <div className="profile-posts-edit-media-card audio">
                <button
                  type="button"
                  className="profile-posts-edit-remove"
                  onClick={handleRemoveExistingAudio}
                  aria-label={t.editModal?.removeAudio || "Remove audio"}
                >
                  ×
                </button>
                <audio controls src={existingAudio.url} />
              </div>
            )}

            {newMedia.map((item, index) => (
              <div key={`new-media-${index}`} className="profile-posts-edit-media-card">
                <button
                  type="button"
                  className="profile-posts-edit-remove"
                  onClick={() => handleRemoveNewMedia(index)}
                  aria-label={t.editModal?.removeMedia || "Remove media"}
                >
                  ×
                </button>
                {item.type === "video" ? (
                  <video src={item.url} controls />
                ) : (
                  <img src={item.url} alt="New media" />
                )}
              </div>
            ))}

            {newAudio && (
              <div className="profile-posts-edit-media-card audio">
                <button
                  type="button"
                  className="profile-posts-edit-remove"
                  onClick={handleRemoveNewAudio}
                  aria-label={t.editModal?.removeAudio || "Remove audio"}
                >
                  ×
                </button>
                <audio controls src={newAudio.url} />
              </div>
            )}

            {totalMediaCount === 0 && (
              <div className="profile-posts-edit-empty">
                {t.editModal?.noMedia || "No media attached to this post."}
              </div>
            )}
          </div>

          {recording && (
            <div className="profile-posts-edit-recording">
              {t.editModal?.recording || "Recording… Tap microphone to stop."}
            </div>
          )}

          <div className="profile-posts-edit-icons">
            <button type="button" className="profile-posts-edit-icon" onClick={() => photoInputRef.current?.click()}>
              <FaRegImages />
            </button>
            <input
              type="file"
              accept="image/*,video/*"
              multiple
              ref={photoInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleMediaInputChange(e, "image")}
            />

            <button type="button" className="profile-posts-edit-icon" onClick={() => cameraInputRef.current?.click()}>
              <FaCamera />
            </button>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={cameraInputRef}
              style={{ display: "none" }}
              onChange={(e) => handleMediaInputChange(e, "image")}
            />

            <button type="button" className="profile-posts-edit-icon" disabled>
              <FaMagic />
            </button>

            <button
              type="button"
              className={`profile-posts-edit-icon ${recording ? "is-recording" : ""}`}
              onClick={handleVoiceClick}
            >
              <FaMicrophone />
            </button>

            <button type="button" className="profile-posts-edit-icon" onClick={() => audioInputRef.current?.click()}>
              <FaRegFileAudio />
            </button>
            <input
              type="file"
              accept="audio/*"
              ref={audioInputRef}
              style={{ display: "none" }}
              onChange={handleAudioFileChange}
            />
          </div>

          {error && <div className="profile-posts-edit-error">{error}</div>}
        </div>

        <div className="profile-posts-edit-footer">
          <button
            type="button"
            className="profile-posts-edit-cancel"
            onClick={closeIfAllowed}
            disabled={submitting}
          >
            {t.editModal?.cancel || "Cancel"}
          </button>
          <button
            type="button"
            className="profile-posts-edit-save"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting
              ? (t.editModal?.submitting || "Saving…")
              : (t.editModal?.save || "Save changes")}
          </button>
        </div>
      </div>
    </div>
  );
}
